import type { Logger } from '../types';

import { TunnelResponse, TunnelResponseTopic } from '@/common/interop/tunnel';
import { tunnel } from './iframe';

/**
 * Represents an interop session instance established via the tunnel.
 */
export class TunnelInstance {
  public id: string;
  public title = document.title;

  constructor(id: string) {
    this.id = id;
  }
}

/**
 * The Tunnel class encapsulates an iframe that navigates to the Connectifi interop host's tunnel endpoint.
 * This endpoint plays a crucial role in validating and authenticating the current user, facilitating
 * the establishment of an interop session.
 *
 * Upon successful session establishment, users gain access to connect to the interop server's WebSocket endpoint.
 * Attempting to connect to this WebSocket endpoint without a previously established interop session through the
 * tunnel will result in a forbidden access response.
 */
export class Tunnel {
  private nextRefresh = 0;
  private refreshSessionTimer: number | undefined;

  constructor(
    private port: MessagePort,
    private logger: Logger,
    private appUrl: string,
    private appTitle: string,
  ) {}

  /**
   * An instance will be instantiated when an interop session is successfully established.
   */
  public instance: TunnelInstance | undefined;

  private createInstance(instanceId: string) {
    this.instance = new TunnelInstance(instanceId);
  }

  private clearRefreshSession() {
    clearInterval(this.refreshSessionTimer); // kill session refresh watcher
  }

  /**
   * An interop session is only valid for limited period of time defined by the server.
   * Once a session has expired the WebSocket is unusable and must be closed.
   * However, by calling the tunnel we can refresh and extend the length of our session.
   *
   * @param expiresMs the UTC time in milliseconds that the session expires.
   */
  private scheduleRefreshSession(refreshInMs: number | undefined) {
    if (!refreshInMs) return;
    this.logger.debug(`schedule session refresh in ${refreshInMs}ms`, {
      refreshInMs,
    });
    this.nextRefresh = new Date().getTime() + refreshInMs;
    this.refreshSessionTimer = window.setInterval(() => {
      const now = new Date().getTime();
      if (now > this.nextRefresh) {
        this.logger.debug(`calling tunnel to refresh session`);
        this.clearRefreshSession(); // tunnel reload will trigger scheduleRefreshSession
        this.touch();
      }
    }, 5000);
  }

  private handleTunnelResponse(response: TunnelResponse) {
    const isStartSession = response.topic === TunnelResponseTopic.StartSession;
    const isRefreshSession =
      response.topic === TunnelResponseTopic.RefreshSession;
    if (isStartSession) {
      this.createInstance(response.instanceId);
    }
    if (isStartSession || isRefreshSession) {
      this.scheduleRefreshSession(response.refreshInMs);
    }
  }

  async resetInstance() {
    if (!this.instance) {
      return;
    }
    // this should ensure a session/instanceId is created
    this.instance.id = '';
  }

  async touch() {
    const response = await tunnel(this.port, {
      instanceId: this.instance?.id,
      instanceTitle: this.appTitle,
      instanceUrl: this.appUrl,
    });
    this.handleTunnelResponse(response);
    return response;
  }
}
